export const S04_EVENT_TYPES = [
    'Executive Conference',
    'Corporate Conference',
    'Public Event',
    'Concert / Festival',
    'Sporting Event',
    'VIP Dinner / Gala',
    'Product Launch',
    'Shareholder Meeting',
    'Other',
] as const;

export const S04_CURRENT_SECURITY = [
    'In-house security',
    'Venue security',
    'Police liaison',
    'CCTV monitoring',
    'Access control / credentialing',
    'Screening / bag checks',
    'Medical / EMS support',
    'Fire / life-safety team',
    'Traffic / parking control',
    'Close protection',
    'None currently',
    'Unknown / consultant to verify',
] as const;

export type S04Intake = {
    esp_event_name: string;
    esp_event_description: string;
    esp_venue_name: string;
    esp_location_address: string;
    esp_location_city: string;
    esp_location_state: string;
    esp_location_zip: string;
    esp_location_country: string;
    esp_location_lat: number | null;
    esp_location_lng: number | null;
    esp_start_date: string;
    esp_end_date: string;
    esp_attendance: string;
    esp_event_type: string;
    esp_vip_attendees: 'yes' | 'no' | '';
    esp_stakeholders: string;
    esp_current_security: string[];
    esp_current_security_details: string;
};

const text = (value: unknown, maximum = 4000) => String(value ?? '').trim().slice(0, maximum);
const coordinate = (value: unknown, minimum: number, maximum: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= minimum && parsed <= maximum ? parsed : null;
};
const values = (value: unknown, allowed: readonly string[]) => Array.isArray(value)
    ? [...new Set(value.map(item => text(item, 100)).filter(item => allowed.includes(item)))].slice(0, allowed.length)
    : [];

export function emptyS04Intake(): S04Intake {
    return {
        esp_event_name: '',
        esp_event_description: '',
        esp_venue_name: '',
        esp_location_address: '',
        esp_location_city: '',
        esp_location_state: '',
        esp_location_zip: '',
        esp_location_country: '',
        esp_location_lat: null,
        esp_location_lng: null,
        esp_start_date: '',
        esp_end_date: '',
        esp_attendance: '',
        esp_event_type: '',
        esp_vip_attendees: '',
        esp_stakeholders: '',
        esp_current_security: [],
        esp_current_security_details: '',
    };
}

export function cleanS04Intake(source: any): S04Intake {
    const input = source && typeof source === 'object' ? source : {};
    const stakeholders = text(input.esp_stakeholders || input.vipAttendees, 2000);
    const vipValue = text(input.esp_vip_attendees || input.vipRequired, 20).toLowerCase();
    const legacySecurity = text(input.esp_access_control || input.esp_staffing || input.currentSecurityArrangements, 4000);
    const currentSecurity = values(input.esp_current_security || input.currentSecurity, S04_CURRENT_SECURITY);
    return {
        esp_event_name: text(input.esp_event_name || input.eventName, 240),
        esp_event_description: text(input.esp_event_description || input.eventDescription || input.description, 4000),
        esp_venue_name: text(input.esp_venue_name || input.venueName || input.event_venue, 240),
        esp_location_address: text(input.esp_location_address || input.address, 500),
        esp_location_city: text(input.esp_location_city || input.city, 160),
        esp_location_state: text(input.esp_location_state || input.state, 160),
        esp_location_zip: text(input.esp_location_zip || input.postalCode || input.zip, 40),
        esp_location_country: text(input.esp_location_country || input.country, 120),
        esp_location_lat: coordinate(input.esp_location_lat ?? input.lat, -90, 90),
        esp_location_lng: coordinate(input.esp_location_lng ?? input.lng, -180, 180),
        esp_start_date: text(input.esp_start_date || input.startAt || input.startDate, 50),
        esp_end_date: text(input.esp_end_date || input.endAt || input.endDate, 50),
        esp_attendance: text(input.esp_attendance || input.expectedAttendance, 20),
        esp_event_type: text(input.esp_event_type || input.eventType, 120),
        esp_vip_attendees: vipValue === 'yes' || vipValue === 'true' ? 'yes'
            : vipValue === 'no' || vipValue === 'false' ? 'no' : stakeholders ? 'yes' : '',
        esp_stakeholders: stakeholders,
        esp_current_security: currentSecurity.length ? currentSecurity : legacySecurity ? ['Unknown / consultant to verify'] : [],
        esp_current_security_details: text(input.esp_current_security_details || legacySecurity, 4000),
    };
}

export function missingS04Intake(source: any): string[] {
    const intake = cleanS04Intake(source);
    const missing: string[] = [];
    if (!intake.esp_event_name || !intake.esp_event_description)
        missing.push('Event Name and Description');
    if (!intake.esp_venue_name || !intake.esp_location_address || !intake.esp_location_country)
        missing.push('Venue Name and Full Address');
    const start = Date.parse(intake.esp_start_date);
    const end = Date.parse(intake.esp_end_date);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start)
        missing.push('Event Dates and Times');
    if (!(Number(intake.esp_attendance) > 0))
        missing.push('Expected Attendance');
    if (!S04_EVENT_TYPES.includes(intake.esp_event_type as any))
        missing.push('Event Type');
    if (!intake.esp_vip_attendees || intake.esp_vip_attendees === 'yes' && !intake.esp_stakeholders)
        missing.push('VIP / Executive Attendees');
    if (!intake.esp_current_security.length)
        missing.push('Current Security Arrangements');
    return missing;
}
